import { ExternalTransferTypes, FeatureFlagTypes } from '@generated/graphql';
import type { SubAccount } from '@interfaces/goalSubAccountTypes';
import type { TranslatedString } from '@interfaces/shared';
import type { Institution } from '@interfaces/transfer';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { MenuItem } from '@mui/material';
import { Icon } from '@ovComponents/1-primative/icon/icon';
import { Form } from '@ovComponents/2-component/form/form';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { AmountField } from '@ovComponents/3-pattern/amountField/amountField';
import ExternalInstitutionSelect from '@ovComponents/3-pattern/legacyExternalInstitutionSelect/externalInstitutionSelect';
import { SubAccountSelect } from '@ovComponents/3-pattern/subaccountSelect/subAccountSelect';
import { dollars2cents } from '@utils/dollars2cents';
import { Box, Button, Grid, Typography, useTheme } from 'hephaestus/web';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLocalization } from '../../../../../../../hooks/useLocalization';
import { useOrganizationFeatureFlag } from '../../../../../../../hooks/useOrganizationFeatureFlag';

export type ExternalTransfer = {
  amountCents: number;
  institution?: Partial<Institution>;
  subAccount?: SubAccount;
  transferAccountNumber: string;
  transferMethod: string;
};
type ExternalTransferDialogContentProps = {
  onTransferCreated: (externalTransfer: ExternalTransfer) => void;
  userData: any;
  onDialogClose: () => void;
  disclaimer?: TranslatedString | null;
};

const ExternalTransferDialogContent = ({
  onTransferCreated,
  userData,
  onDialogClose,
  disclaimer,
}: ExternalTransferDialogContentProps) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation(['workflowCompletions', 'affiliationTypes']);
  const [inboundAccount, setInboundAccount] = useState<
    SubAccount | undefined
  >();
  const [transferMethod, setTransferMethod] = useState<ExternalTransferTypes>(
    ExternalTransferTypes.AllInCash,
  );
  const [institution, setInstitution] = useState<
    Partial<Institution> | undefined
  >();
  const [externalAccountNumber, setExternalAccountNumber] = useState('');
  const [amount, setAmount] = useState('');
  const enableInKind = useOrganizationFeatureFlag(
    FeatureFlagTypes.InKindTransfers,
  );
  const theme = useTheme();
  const submit = () => {
    onTransferCreated({
      amountCents: dollars2cents(amount),
      institution,
      subAccount: inboundAccount,
      transferAccountNumber: externalAccountNumber,
      transferMethod,
    });
  };
  return (
    <Form onSubmit={submit}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'end',
        }}
      >
        <Box
          onClick={onDialogClose}
          sx={{
            display: 'flex',
            cursor: 'pointer',
          }}
        >
          <Icon icon={CloseRoundedIcon} size='medium' />
        </Box>
      </Box>
      <Typography variant='headingMedium' sx={{ mt: 1 }}>
        {t('workflowCompletions:externalTransferCreation.title')}
      </Typography>
      <SubAccountSelect
        sx={{ mt: '24px' }}
        omitAny
        selectedSubAccount={inboundAccount}
        onSubAccountSelect={setInboundAccount}
        userId={userData.id}
        name='inboundAccount'
        label={t('workflowCompletions:inboundAccount')}
      />
      <SelectField
        onChange={(e: any) => setTransferMethod(e.target.value)}
        name='transferMethod'
        label={t('workflowCompletions:transferMethod')}
        fullWidth
        sx={{ mt: '24px' }}
        value={transferMethod}
      >
        <MenuItem value='ALL_IN_CASH'>
          {t('workflowCompletions:transferMethodOptions.ALL_IN_CASH')}
        </MenuItem>
        <MenuItem value='PARTIAL_CASH'>
          {t('workflowCompletions:transferMethodOptions.PARTIAL_CASH')}
        </MenuItem>
        {enableInKind && (
          <MenuItem value='ALL_IN_KIND'>
            {t('workflowCompletions:transferMethodOptions.ALL_IN_KIND')}
          </MenuItem>
        )}
      </SelectField>
      <Grid
        container
        spacing={1}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'end',
          mt: '20px',
        }}
      >
        <Grid size={8}>
          <ExternalInstitutionSelect
            setInstitution={(e) => setInstitution(e)}
            value={institution?.id ?? ''}
            name='institution'
            label={t('workflowCompletions:externalFinancialInstitution')}
            useOvComponents
          />
        </Grid>
        <Grid size={4}>
          <TextField
            onChange={(e: any) => setExternalAccountNumber(e.target.value)}
            name='transferAccountNumber'
            label={t('workflowCompletions:externalAccount')}
            fullWidth
            value={externalAccountNumber}
          />
        </Grid>
      </Grid>
      <Grid
        container
        spacing={1}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'end',
          mt: '20px',
        }}
      >
        <Grid size={4}>
          <AmountField
            name='amount'
            setAmount={(e: any) => setAmount(e)}
            amount={amount}
          />
        </Grid>
      </Grid>
      <br></br>
      {disclaimer && (
        <Box
          sx={{
            mb: 1,
            background: theme.color.surfaceContainerVariant,
            borderRadius: theme.borderRadius.lg,
            p: 2,
          }}
        >
          <Typography variant='bodySmall' component='div'>
            <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
              {translateBackendString(disclaimer)}
            </ReactMarkdown>
          </Typography>
        </Box>
      )}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'end',
        }}
      >
        <Button color='primary' disabled={false} sx={{ mt: 3 }} type='submit'>
          {t('workflowCompletions:addTransfer')}
        </Button>
      </Box>
    </Form>
  );
};

export default ExternalTransferDialogContent;
